// src/services/patientsService.ts (frontend)
import { injectable } from 'inversify';

import { ColumnSort } from '@modulate/shared/src/constants/column-sort';
import { IDataQueryRequest, IDataQueryResponse } from '@modulate/shared/src/models/data-query';
import { IPatient, IPatientSummary } from '@modulate/shared/src/models/patient';
import { useAuth } from '@src/contexts/authContext';
import api from '../axios-config';

export const PATIENTS_SERVICE = Symbol.for('PATIENTS_SERVICE');

export interface IPatientsService {
  create(patient: IPatient): Promise<IPatient>;
  getById(id: string, clinicId: number): Promise<IPatient>;
  getSummaries(params: {
    clinicId: number;
    searchTerm?: string;
    sortModel?: { fieldName: string; sort: ColumnSort };
    startRow: number;
    endRow: number;
  }): Promise<IDataQueryResponse<IPatientSummary>>;
}

@injectable()
export class PatientsService implements IPatientsService {
  authContext = useAuth();
  async getSummaries(params: {
    clinicId: number;
    searchTerm?: string;
    sortModel?: { fieldName: string; sort: ColumnSort };
    startRow: number;
    endRow: number;
  }): Promise<IDataQueryResponse<IPatientSummary>> {
    const response = await api.get<IDataQueryResponse<IPatientSummary>>(`/api/${params.clinicId}/patients`, {
      params: {
        searchTerm: params.searchTerm,
        sortModel: JSON.stringify(params.sortModel),
        startRow: params.startRow,
        endRow: params.endRow,
      },
    });
    return response.data;
  }

  async create(patient: IPatient): Promise<IPatient> {
    const payload: IPatient = { ...patient, clinicId: this.authContext.userObject.clinicId } as IPatient;
    const response = await api.post<IPatient>('/api/patients', payload);
    return response.data;
  }
  async getById(id: string, clinicId: number): Promise<IPatient> {
    const response = await api.get<IPatient>(`/api/patients/${clinicId}/${id}`);
    return this.transformResponseToPatient(response.data);
  }
  private transformResponseToPatient(apiPatient: any): IPatient {
    const [firstName, lastName] = this.extractName((apiPatient?.name ?? '').toString());
    return {
      id: apiPatient.id?.toString(),
      clinicId: this.authContext.userObject.clinicId, // TODO : Change after logging and state management is implemented
      firstName: firstName,
      lastName: lastName,
      dateOfBirth: new Date(apiPatient.date_of_birth),
      email: apiPatient.email,
      phoneNumber: apiPatient.phone,
      hasNoHealthInsurance: apiPatient.insurance === null,
      insuranceInfo: undefined,
    };
  }

  private extractName(fullName: string): [string, string] {
    if (!fullName || !fullName.trim()) {
      return ['', ''];
    }
    const nameParts = fullName.trim().split(/\s+/);
    const lastName = nameParts.pop() || '';
    const firstName = nameParts.join(' ');
    return [firstName, lastName];
  }

  // Add more methods as needed, like updatePatient, deletePatient, etc.
}
